import { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { SET_REGISTRY } from './setRegistry';
import { imageUrl, isLandscapeCard } from './CardImage';
import { getParallelsForType, parallelKey } from './parallelData';
import { getParallelsForSet } from './sets/parallelsConfig';
import { lightAccent } from './theme';
import GalleryLightbox from './GalleryLightbox';

// Sentinel parallel-filter value meaning "every parallel at once".
export const PARALLEL_ALL = '__all__';

// Build the gallery tiles for a single card within a single sub-set.
// Returns one tile per owned physical card (base + each parallel/serial variant)
// according to the active parallel filter.
export function buildTilesForCard(setIdFull, subsetId, card, parallels, parallelFilter, states) {
  const baseKey = `${setIdFull}__${subsetId}__${card.n}`;
  const tiles = [];

  // Sketch entries hang `__s__<i>` copies directly off the base key (no parallel
  // segment) — emit one tile per entry, suppressing the parent row's tile, the
  // same way collectParallel treats serial copies.
  const baseEntryKeys = Object.keys(states).filter(k => k.startsWith(baseKey + '__s__'));

  if (parallelFilter === 'base') {
    if (baseEntryKeys.length === 0) {
      tiles.push({ card, cardKey: baseKey, state: states[baseKey] || null, parallel: null });
    } else {
      baseEntryKeys.forEach(k => tiles.push({ card, cardKey: k, state: states[k] || 'collected', parallel: null }));
    }
    return tiles;
  }

  // Emit a tile for the base parallel key (if owned and no serials logged) plus
  // one per logged serial. Returns true if anything was owned for this parallel.
  const collectParallel = (parallel) => {
    const pBaseKey = parallelKey(`${setIdFull}__${subsetId}`, card.n, parallel.id);
    const serialKeys = Object.keys(states).filter(k => k.startsWith(pBaseKey + '__s__'));
    if (states[pBaseKey] && serialKeys.length === 0) {
      tiles.push({ card, cardKey: pBaseKey, state: states[pBaseKey], parallel });
    }
    serialKeys.forEach(k => {
      tiles.push({ card, cardKey: k, state: states[k] || 'collected', parallel });
    });
    return serialKeys.length > 0 || !!states[pBaseKey];
  };

  if (parallelFilter === PARALLEL_ALL) {
    // "All parallels" shows OWNED cards only (base + every parallel variant).
    // Emitting placeholders for every unowned parallel would be thousands of tiles.
    if (states[baseKey] && baseEntryKeys.length === 0) tiles.push({ card, cardKey: baseKey, state: states[baseKey], parallel: null });
    baseEntryKeys.forEach(k => tiles.push({ card, cardKey: k, state: states[k] || 'collected', parallel: null }));
    parallels.forEach(collectParallel);
    return tiles;
  }

  // A single specific parallel — keep the original behaviour of showing a
  // placeholder tile when nothing is owned, so "All Cards" still lists it.
  const parallel = parallels.find(p => p.id === parallelFilter);
  if (!parallel) return [];
  const owned = collectParallel(parallel);
  if (!owned) {
    tiles.push({ card, cardKey: parallelKey(`${setIdFull}__${subsetId}`, card.n, parallel.id), state: null, parallel });
  }
  return tiles;
}

const SET_TYPE_OPTIONS = [
  { id: 'base', label: 'Base Set' },
  { id: 'insert', label: 'Inserts' },
  { id: 'auto', label: 'Autographs' },
  { id: 'sketch', label: 'Sketch Cards' },
];

const CARD_FILTER_OPTIONS = [
  { id: 'collected', label: 'Collected' },
  { id: 'wishlist', label: 'Wishlist' },
  { id: 'trade', label: 'For Trade' },
  { id: 'for_sale', label: 'For Sale' },
  { id: 'all', label: 'All Cards' },
];

function GalleryCard({ card, cardKey, userId, state, parallel, printRun, sketchInfo, onView, onUrl }) {
  const serialMatch = cardKey.match(/__s__(\d+)$/);
  const serialNum = serialMatch ? serialMatch[1] : null;
  // sketchInfo is set (possibly {}) only for sketch entry tiles: show the
  // subject/foil instead of a #N/printRun serial badge.
  const serialDisplay = serialNum
    ? (sketchInfo
        ? ([sketchInfo.subject, sketchInfo.parallel].filter(Boolean).join(' · ') || 'SKETCH')
        : (printRun ? `#${serialNum}/${printRun}` : `#${serialNum}`))
    : null;
  const [frontUrl, setFrontUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Resolved URL (or null) is reported up so the shared lightbox can page
    // across every tile with a photo, not just this one.
    const settle = (url) => { setFrontUrl(url); setLoaded(true); onUrl?.(cardKey, url); };
    const primaryUrl = imageUrl(userId, cardKey, 'front');
    const img = new Image();
    img.onload = () => settle(primaryUrl);
    img.onerror = () => {
      // If this is a serial key, try the base parallel URL once
      if (cardKey.includes('__s__')) {
        const baseKey = cardKey.split('__s__')[0];
        const fallbackUrl = imageUrl(userId, baseKey, 'front');
        const img2 = new Image();
        img2.onload = () => settle(fallbackUrl);
        img2.onerror = () => settle(null);
        img2.src = fallbackUrl;
      } else {
        settle(null);
      }
    };
    img.src = primaryUrl;
  }, [cardKey, userId]);

  return (
    <>
      <div style={styles.card} onClick={() => frontUrl && onView()}>
        <div style={styles.cardImageWrap}>
          {!loaded && <div style={styles.cardPlaceholder} />}
          {loaded && frontUrl && (
            <img
              src={frontUrl}
              alt={card.name}
              style={{ ...styles.cardImage, ...(isLandscapeCard(cardKey) ? { objectFit: 'contain' } : {}) }}
            />
          )}
          {loaded && !frontUrl && (
            <div style={styles.cardMissing}>
              <svg viewBox="0 0 100 140" style={{ width: '100%', height: '100%', opacity: 0.15 }}>
                <line x1="5" y1="5" x2="95" y2="135" stroke="var(--text)" strokeWidth="2"/>
                <line x1="95" y1="5" x2="5" y2="135" stroke="var(--text)" strokeWidth="2"/>
                <rect x="2" y="2" width="96" height="136" rx="4" fill="none" stroke="var(--border)" strokeWidth="1.5"/>
              </svg>
            </div>
          )}
          {state && (
            <div style={{ ...styles.statePip, background: STATE_COLORS[state] }} />
          )}
        </div>
        <div style={styles.cardCaption}>
          <div style={styles.cardNum}>{card.n}{serialDisplay ? ` · ${serialDisplay}` : ''}</div>
          <div style={styles.cardName}>{card.name}</div>
          {parallel && (
            <div style={{ marginTop: '3px' }}>
              <span style={{ fontFamily: "'Orbitron',monospace", fontSize: '7px', letterSpacing: '0.05em', padding: '1px 5px', border: '1px solid', borderRadius: '2px', borderColor: lightAccent(parallel.color || '#c8d8f0') + '66', color: lightAccent(parallel.color || '#c8d8f0'), background: lightAccent(parallel.color || '#c8d8f0') + '12' }}>
                {parallel.name}{parallel.print_run ? ` /${parallel.print_run}` : ''}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

const STATE_COLORS = {
  collected: 'var(--green)',
  wishlist: 'var(--gold)',
  trade: 'var(--cyan)',
  for_sale: 'var(--purple)',
};

export default function Gallery({ user, profile, setMeta = null, SETS, cardStates, sketchMeta = {}, onClose }) {
  const [selectedTypes, setSelectedTypes] = useState(['base']);
  const [cardFilter, setCardFilter] = useState('collected');
  const [parallelFilter, setParallelFilter] = useState('base'); // 'base', a parallel id, or PARALLEL_ALL
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Multi-set support: track which sets are shown and cache each set's data.
  // The originating set is seeded from props (already loaded by Checklist).
  // With no setMeta the gallery is standalone (opened from the nav drawer on a
  // non-checklist page) and self-loads every set the collector has cards in.
  const [selectedSetIds, setSelectedSetIds] = useState(setMeta ? [setMeta.id] : []);
  const [loadedSets, setLoadedSets] = useState(() => (setMeta ? {
    [setMeta.id]: { meta: setMeta, subsets: SETS || [], cardStates: cardStates || {}, sketchMeta: sketchMeta || {} },
  } : {}));
  const [loadingSetId, setLoadingSetId] = useState(null);
  const [setPickerOpen, setSetPickerOpen] = useState(false);
  // Shared lightbox: index into allCards | null. Each card reports its
  // resolved front URL into the ref so the lightbox can page across tiles
  // (a ref, not state — hundreds of tiles resolve and re-rendering the whole
  // grid per image would be wasteful).
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const tileUrls = useRef({});

  // Keep the originating set's cached data fresh as Checklist finishes loading it.
  useEffect(() => {
    if (!setMeta) return;
    setLoadedSets(prev => ({
      ...prev,
      [setMeta.id]: { meta: setMeta, subsets: SETS || [], cardStates: cardStates || {}, sketchMeta: sketchMeta || {} },
    }));
  }, [setMeta, SETS, cardStates, sketchMeta]);

  // Standalone mode: one query for every collection row, then load the data
  // file of each set the collector actually has cards in.
  useEffect(() => {
    if (setMeta) return;
    let cancelled = false;
    (async () => {
      setLoadingSetId('__all__');
      try {
        const { data } = await supabase.from('collections')
          .select('card_key, status, sketch_subject, sketch_parallel')
          .eq('user_id', user.id);
        const bySet = {};
        (data || []).forEach(row => {
          const sid = row.card_key.split('__')[0];
          (bySet[sid] = bySet[sid] || []).push(row);
        });
        const owned = SET_REGISTRY.filter(s => bySet[s.id]);
        const results = await Promise.all(owned.map(async (meta) => {
          try {
            const module = await import(`./sets/${meta.dataFile}.js`);
            const states = {};
            const sketchMap = {};
            bySet[meta.id].forEach(row => {
              states[row.card_key] = row.status;
              if (row.sketch_subject || row.sketch_parallel) sketchMap[row.card_key] = { subject: row.sketch_subject || '', parallel: row.sketch_parallel || '' };
            });
            return [meta.id, { meta, subsets: module.SETS || [], cardStates: states, sketchMeta: sketchMap }];
          } catch (e) { console.error('Failed to load set for gallery:', e); return null; }
        }));
        if (cancelled) return;
        const next = {};
        results.filter(Boolean).forEach(([id, val]) => { next[id] = val; });
        setLoadedSets(prev => ({ ...next, ...prev }));
        setSelectedSetIds(Object.keys(next));
      } finally {
        if (!cancelled) setLoadingSetId(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Add/remove a set from the gallery. Lazily imports the set's card data and
  // queries that set's collection rows the first time it's selected.
  const toggleSet = async (sid) => {
    if (selectedSetIds.includes(sid)) {
      if (selectedSetIds.length === 1) return; // keep at least one set selected
      setSelectedSetIds(prev => prev.filter(x => x !== sid));
      return;
    }
    setSelectedSetIds(prev => [...prev, sid]);
    if (loadedSets[sid]) return;
    const meta = SET_REGISTRY.find(s => s.id === sid);
    if (!meta) return;
    setLoadingSetId(sid);
    try {
      const [module, { data }] = await Promise.all([
        import(`./sets/${meta.dataFile}.js`),
        supabase.from('collections').select('card_key, status, sketch_subject, sketch_parallel').eq('user_id', user.id).like('card_key', `${sid}__%`),
      ]);
      const states = {};
      const sketchMap = {};
      (data || []).forEach(row => {
        states[row.card_key] = row.status;
        if (row.sketch_subject || row.sketch_parallel) sketchMap[row.card_key] = { subject: row.sketch_subject || '', parallel: row.sketch_parallel || '' };
      });
      setLoadedSets(prev => ({ ...prev, [sid]: { meta, subsets: module.SETS || [], cardStates: states, sketchMeta: sketchMap } }));
    } catch (e) {
      console.error('Failed to load set for gallery:', e);
      setSelectedSetIds(prev => prev.filter(x => x !== sid)); // roll back on failure
    } finally {
      setLoadingSetId(null);
    }
  };

  const allSetIds = SET_REGISTRY.map(s => s.id);
  const allSelected = selectedSetIds.length === allSetIds.length;

  // "All Sets" toggle — select every set (loading any not yet cached) or, when
  // already all-selected, collapse back to just the originating set.
  const toggleAllSets = async () => {
    if (allSelected) {
      setSelectedSetIds(prev => (setMeta ? [setMeta.id] : prev.slice(0, 1)));
      return;
    }
    setSelectedSetIds(allSetIds);
    const missing = SET_REGISTRY.filter(s => !loadedSets[s.id]);
    if (missing.length === 0) return;
    setLoadingSetId('__all__');
    try {
      const results = await Promise.all(missing.map(async (meta) => {
        try {
          const [module, { data }] = await Promise.all([
            import(`./sets/${meta.dataFile}.js`),
            supabase.from('collections').select('card_key, status, sketch_subject, sketch_parallel').eq('user_id', user.id).like('card_key', `${meta.id}__%`),
          ]);
          const states = {};
          const sketchMap = {};
          (data || []).forEach(row => {
            states[row.card_key] = row.status;
            if (row.sketch_subject || row.sketch_parallel) sketchMap[row.card_key] = { subject: row.sketch_subject || '', parallel: row.sketch_parallel || '' };
          });
          return [meta.id, { meta, subsets: module.SETS || [], cardStates: states, sketchMeta: sketchMap }];
        } catch (e) { console.error('Failed to load set for gallery:', e); return null; }
      }));
      setLoadedSets(prev => {
        const next = { ...prev };
        results.filter(Boolean).forEach(([id, val]) => { next[id] = val; });
        return next;
      });
    } finally {
      setLoadingSetId(null);
    }
  };

  const toggleType = (id) => setSelectedTypes(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  // The sets currently displayed (loaded + selected), and their merged collection
  // states. Card keys are namespaced by set id, so merging never collides.
  const activeSets = selectedSetIds.map(id => loadedSets[id]).filter(Boolean);
  const combinedStates = Object.assign({}, ...activeSets.map(s => s.cardStates));
  const combinedSketchMeta = Object.assign({}, ...activeSets.map(s => s.sketchMeta || {}));

  // Build parallel options across every active set + selected sub-set type.
  const availableParallels = [];
  activeSets.forEach(s => {
    s.subsets.filter(ss => selectedTypes.includes(ss.type)).forEach(ss => {
      const ps = getParallelsForSet(s.meta.id, ss.type, ss) || getParallelsForType(ss.type) || [];
      ps.forEach(p => { if (!availableParallels.find(x => x.id === p.id)) availableParallels.push(p); });
    });
  });

  // Build visible cards across every active set.
  const allCards = activeSets.flatMap(s =>
    s.subsets.filter(ss => selectedTypes.includes(ss.type)).flatMap(ss => {
      const parallels = getParallelsForSet(s.meta.id, ss.type, ss) || getParallelsForType(ss.type) || [];
      return ss.cards.flatMap(card =>
        buildTilesForCard(s.meta.id, ss.id, card, parallels, parallelFilter, combinedStates)
          .map(t => ({ ...t, setMeta: s.meta }))
      );
    })
  ).filter(({ state }) => {
    if (cardFilter === 'all') return true;
    return state === cardFilter;
  });

  const primarySetId = selectedSetIds[0] || setMeta?.id || '';
  const galleryUrl = (() => {
    const params = new URLSearchParams();
    params.set('filter', cardFilter);
    params.set('types', selectedTypes.join(','));
    params.set('parallel', parallelFilter === PARALLEL_ALL ? 'all' : parallelFilter);
    if (selectedSetIds.length > 1) params.set('sets', selectedSetIds.join(','));
    params.set('locked', '1');
    return `${window.location.origin}/gallery/${profile?.username || ''}/${primarySetId}?${params.toString()}`;
  })();



  return (
    <div style={styles.overlay}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}><img src='/logo.png' style={{width:'36px',height:'36px',borderRadius:'6px',marginRight:'10px',flexShrink:0}} alt='CK' /></div><div style={styles.headerLeft}>
          <div style={styles.title}>GALLERY</div>
          <div style={styles.subtitle}>{profile?.username} · {selectedSetIds.length > 1 ? `${selectedSetIds.length} collections` : (loadedSets[selectedSetIds[0]]?.meta.shortName || setMeta?.shortName || '…')}</div>
        </div>
        <div style={styles.headerRight}>
          {/* Share buttons */}
          <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent('Check out my gallery! ' + galleryUrl)}`} target="_blank" rel="noreferrer" style={{ ...styles.shareBtn, background: '#25d366' }}>WhatsApp</a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(galleryUrl)}`} target="_blank" rel="noreferrer" style={{ ...styles.shareBtn, background: '#1877f2' }}>Facebook</a>
          <button style={{ ...styles.shareBtn, background: '#5865f2', cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText('Check out my gallery!\n' + galleryUrl); alert('Copied for Discord!'); }}>Discord</button>
          <button style={{ ...styles.shareBtn, background: 'var(--muted-2)', cursor: 'pointer' }} onClick={() => {
            navigator.clipboard.writeText(galleryUrl);
          }}>Copy Link</button>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filtersContainer}>
        <button style={styles.filtersToggle} onClick={() => setFiltersOpen(o => !o)}>FILTERS {filtersOpen ? '▲' : '▼'}</button>
      </div>
      {filtersOpen && <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>SHOW:</span>
          {CARD_FILTER_OPTIONS.map(f => (
            <button
              key={f.id}
              style={{ ...styles.filterBtn, ...(cardFilter === f.id ? styles.filterBtnActive : {}) }}
              onClick={() => setCardFilter(f.id)}
            >{f.label}</button>
          ))}
        </div>
        <div style={{ ...styles.filterGroup, position: 'relative' }}>
          <span style={styles.filterLabel}>COLLECTIONS:</span>
          <button
            style={{ ...styles.filterBtn, ...styles.filterBtnActive, display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => setSetPickerOpen(o => !o)}
          >
            {allSelected
              ? 'All sets'
              : selectedSetIds.length <= 1
                ? (loadedSets[selectedSetIds[0]]?.meta.shortName || setMeta?.shortName || '…')
                : `${selectedSetIds.length} collections`}
            <span style={{ fontSize: '9px' }}>{setPickerOpen ? '▲' : '▼'}</span>
          </button>
          {setPickerOpen && (
            <>
              <div style={styles.setDropdownBackdrop} onClick={() => setSetPickerOpen(false)} />
              <div style={styles.setDropdown}>
                <button
                  style={{ ...styles.setDropdownItem, fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '1px', color: allSelected ? 'var(--cyan)' : 'var(--muted)', borderBottom: '1px solid var(--border)', borderRadius: 0, ...(loadingSetId === '__all__' ? { opacity: 0.5 } : {}) }}
                  onClick={toggleAllSets}
                >
                  <span style={{ width: '14px', display: 'inline-block' }}>{allSelected ? '☑' : '☐'}</span>
                  ALL SETS{loadingSetId === '__all__' ? ' …' : ''}
                </button>
                {[...SET_REGISTRY].sort((a, b) => (b.year || 0) - (a.year || 0)).map(s => {
                  const active = selectedSetIds.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      style={{ ...styles.setDropdownItem, ...(active ? { color: 'var(--cyan)' } : {}), ...(loadingSetId === s.id ? { opacity: 0.5 } : {}) }}
                      onClick={() => toggleSet(s.id)}
                    >
                      <span style={{ width: '14px', display: 'inline-block' }}>{active ? '☑' : '☐'}</span>
                      {s.shortName}{loadingSetId === s.id ? ' …' : ''}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={styles.filterGroup}>
          <span style={styles.filterLabel}>SETS:</span>
          {SET_TYPE_OPTIONS.map(f => (
            <button
              key={f.id}
              style={{ ...styles.filterBtn, ...(selectedTypes.includes(f.id) ? styles.filterBtnActive : {}) }}
              onClick={() => toggleType(f.id)}
            >{f.label}</button>
          ))}
        </div>
        {availableParallels.length > 0 && (
          <div style={styles.filterGroup}>
            <span style={styles.filterLabel}>PARALLEL:</span>
            <button
              style={{ ...styles.filterBtn, ...(parallelFilter === 'base' ? styles.filterBtnActive : {}) }}
              onClick={() => setParallelFilter('base')}
            >Base Cards</button>
            <button
              style={{ ...styles.filterBtn, ...(parallelFilter === PARALLEL_ALL ? styles.filterBtnActive : {}) }}
              onClick={() => setParallelFilter(PARALLEL_ALL)}
            >All Parallels</button>
            {availableParallels.some(p => p.group)
              ? [...new Set(availableParallels.map(p => p.group))].map(group => (
                  <span key={group} style={{ display: 'contents' }}>
                    <span style={styles.parallelGroupLabel}>{group}</span>
                    {availableParallels.filter(p => p.group === group).map(p => (
                      <button
                        key={p.id}
                        style={{ ...styles.filterBtn, ...(parallelFilter === p.id ? { borderColor: lightAccent(p.color), color: lightAccent(p.color), background: lightAccent(p.color) + '15' } : {}) }}
                        onClick={() => setParallelFilter(p.id)}
                      >{p.name}{p.print_run ? ` /${p.print_run}` : ''}{p.odds ? ` (${p.odds})` : ''}</button>
                    ))}
                  </span>
                ))
              : availableParallels.map(p => (
                  <button
                    key={p.id}
                    style={{ ...styles.filterBtn, ...(parallelFilter === p.id ? { borderColor: lightAccent(p.color), color: lightAccent(p.color), background: lightAccent(p.color) + '15' } : {}) }}
                    onClick={() => setParallelFilter(p.id)}
                  >{p.name}{p.print_run ? ` /${p.print_run}` : ''}</button>
                ))
            }
          </div>
        )}
        <div style={styles.cardCount}>{allCards.length} CARDS</div>
      </div>}

      {/* Gallery grid */}
      <div style={styles.grid}>
        {allCards.length === 0 ? (
          <div style={styles.empty}>{loadingSetId ? 'Loading your collection…' : 'No cards match your filters'}</div>
        ) : (
          allCards.map(({ card, cardKey, state, parallel }, i) => (
            <GalleryCard
              key={cardKey}
              card={card}
              cardKey={cardKey}
              userId={user.id}
              state={state}
              parallel={parallel}
              printRun={parallel?.print_run}
              sketchInfo={!parallel && cardKey.includes('__s__') ? (combinedSketchMeta[cardKey] || {}) : null}
              onView={() => setLightboxIdx(i)}
              onUrl={(key, url) => { tileUrls.current[key] = url; }} />
          ))
        )}
      </div>

      {lightboxIdx != null && (
        <GalleryLightbox
          tiles={allCards}
          index={lightboxIdx}
          urlFor={(t) => tileUrls.current[t.cardKey]}
          captionFor={({ card, cardKey, parallel }) => {
            // Mirrors the tile caption: serial (or sketch subject/foil) +
            // parallel chip text + card name.
            const serialNum = cardKey.match(/__s__(\d+)$/)?.[1] || null;
            const sketchInfo = !parallel && cardKey.includes('__s__') ? (combinedSketchMeta[cardKey] || {}) : null;
            const serialDisplay = serialNum
              ? (sketchInfo
                  ? ([sketchInfo.subject, sketchInfo.parallel].filter(Boolean).join(' · ') || 'SKETCH')
                  : (parallel?.print_run ? `#${serialNum}/${parallel.print_run}` : `#${serialNum}`))
              : null;
            return `${card.n}${serialDisplay ? ` · ${sketchInfo ? '' : 'Serial '}${serialDisplay}` : ''}${parallel ? ` · ${parallel.name}${parallel.print_run ? ` /${parallel.print_run}` : ''}` : ''} — ${card.name}`;
          }}
          onNavigate={setLightboxIdx}
          onClose={() => setLightboxIdx(null)}
        />
      )}
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'var(--bg)',
    zIndex: 9999,
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    // Fixed overlays escape the body's safe-area padding (theme.css) — add it here
    // or the header sits under the native status bar / Dynamic Island.
    paddingTop: 'max(env(safe-area-inset-top, 0px), var(--ck-android-inset-top, 0px))',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '1px solid var(--border)',
    background: 'rgba(0,0,0,0.4)',
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', flexDirection: 'column', gap: '2px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: '60%' },
  title: { fontFamily: "'Orbitron', monospace", fontSize: '16px', fontWeight: 900, letterSpacing: '4px', color: 'var(--text)' },
  subtitle: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted-3)' },
  shareBtn: {
    fontFamily: "'Orbitron', monospace", fontSize: '8px', letterSpacing: '1px',
    padding: '6px 12px', border: 'none', borderRadius: '3px',
    color: '#fff', textDecoration: 'none', cursor: 'pointer',
  },
  closeBtn: {
    background: 'none', border: '1px solid var(--border)', color: 'var(--muted-2)',
    cursor: 'pointer', fontSize: '14px', width: '32px', height: '32px',
    borderRadius: '2px', marginLeft: '8px',
  },
  filtersContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '4px 16px 0',
    background: '#0a0e1a',
  },
  filtersToggle: {
    background: 'transparent',
    border: '1px solid var(--cyan)44',
    color: 'var(--cyan)',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.08em',
    padding: '4px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filters: {
    display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
    padding: '12px 24px',
    borderBottom: '1px solid var(--border)',
    background: 'rgba(0,0,0,0.2)',
    flexShrink: 0,
  },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' },
  filterLabel: { fontFamily: "'Orbitron', monospace", fontSize: '10px', letterSpacing: '2px', color: 'var(--muted)', marginRight: '2px' },
  filterBtn: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '11px',
    padding: '4px 10px', background: 'transparent',
    border: '1px solid var(--border)', color: 'var(--muted-2)',
    cursor: 'pointer', borderRadius: '2px',
  },
  parallelGroupLabel: {
    fontFamily: "'Orbitron', monospace", fontSize: '8px',
    letterSpacing: '2px', color: 'var(--muted-2)',
    padding: '4px 8px', alignSelf: 'center',
    borderLeft: '2px solid var(--border)', marginLeft: '4px',
  },
  filterBtnActive: { borderColor: 'var(--cyan)', color: 'var(--cyan)', background: 'rgba(var(--cyan-rgb),0.08)' },
  setDropdownBackdrop: { position: 'fixed', inset: 0, zIndex: 40 },
  setDropdown: {
    position: 'absolute', top: '100%', left: '0', marginTop: '6px',
    zIndex: 50, minWidth: '220px', maxHeight: '320px', overflowY: 'auto',
    background: '#0a0e1a', border: '1px solid var(--border)', borderRadius: '6px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)', padding: '4px',
    display: 'flex', flexDirection: 'column',
  },
  setDropdownItem: {
    fontFamily: "'Share Tech Mono', monospace", fontSize: '12px',
    textAlign: 'left', padding: '7px 10px', background: 'transparent',
    border: 'none', color: 'var(--text)', cursor: 'pointer', borderRadius: '4px',
    display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap',
  },
  cardCount: { fontFamily: "'Share Tech Mono', monospace", fontSize: '11px', color: 'var(--muted)', marginLeft: 'auto' },
  grid: {
    flex: 1, overflowY: 'auto',
    padding: '24px',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '20px',
    alignContent: 'start',
  },
  card: { display: 'flex', flexDirection: 'column', gap: '8px', cursor: 'pointer' },
  cardImageWrap: {
    position: 'relative',
    width: '100%', paddingBottom: '140%',
    background: 'var(--panel-deep)',
    border: '1px solid var(--border)',
    borderRadius: '4px', overflow: 'hidden',
  },
  cardPlaceholder: { position: 'absolute', inset: 0, background: 'var(--panel-deep)' },
  cardImage: { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' },
  cardMissing: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px' },
  statePip: { position: 'absolute', top: '6px', right: '6px', width: '8px', height: '8px', borderRadius: '50%' },
  cardCaption: { textAlign: 'center' },
  cardNum: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--muted)' },
  cardName: { fontFamily: "'Share Tech Mono', monospace", fontSize: '10px', color: 'var(--text)', lineHeight: 1.3 },
  empty: { gridColumn: '1/-1', textAlign: 'center', fontFamily: "'Share Tech Mono', monospace", fontSize: '12px', color: 'var(--muted)', padding: '60px 0' },
};
